import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'store';

/** A bundle the customer has a relationship with, as the chooser needs to describe it. */
export type RelatedBundle = {
  bundleId: string;
  name: string;
  tier: string | null;
  memberCourseIds: string[];
  memberSlugs: string[];
  accessType: 'purchase' | 'subscription' | 'installment' | 'trial';
};

/**
 * The learner-state resolver behind the upgrade chooser.
 *
 * Which upgrade flow a learner should see is a fact about the LEARNER — what they are trialling,
 * what they have paid for, whether a plan is running — and no course-level flag can answer it.
 * The server computes it (GET /courses/bundle-builder/context) from the same billing tables every
 * access gate reads, so the chooser can never disagree with the LMS about what someone holds.
 */
export type BundleBuilderContext = {
  trialBundle: RelatedBundle | null;
  paidBundles: RelatedBundle[];
  activeInstallmentBundle: RelatedBundle | null;
  certLevels: Record<string, string>;
  ownsEverything: boolean;
};

export const useBundleBuilderContext = (enabled: boolean) => {
  const accessToken = useSelector(selectors.auth.selectAccessToken);
  const [context, setContext] = useState<BundleBuilderContext | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAccessToken = useCallback(
    () =>
      accessToken ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      '',
    [accessToken],
  );

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:4000';

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${baseUrl}/courses/bundle-builder/context`, {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error(`Context request failed (${response.status})`);

        const data: BundleBuilderContext = await response.json();
        if (!cancelled) setContext(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load your options');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, getAccessToken]);

  return { context, isLoading, error };
};

/**
 * The certification ids the learner holds at a PAID level.
 *
 * certLevels is derived from getPaymentLevel, which reads only paid sources (purchases, plans,
 * active subscriptions) — a trial-only account verifiably resolves every cert to 'none' — so no
 * further trial filtering is needed here.
 */
export const ownedCertIdsFrom = (context: BundleBuilderContext | null): string[] => {
  if (!context) return [];
  return Object.entries(context.certLevels)
    .filter(([, level]) => level !== 'none' && level !== 'paused')
    .map(([courseId]) => courseId);
};
